import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	Commands,
	ModelSelectors,
	UiStateSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { addPrefix } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { Wizard } from "@com.mgmtp.a12.widgets/widgets-core/lib/wizard/main/wizard.view";

import { Task, TaskKey } from "../../../types/task";
import { useConfigValueFromFVManifest } from "../../../utilities/modulfFVManifestHelpers";
import { WIZARD_TASKS_FVMANIFEST_KEY } from "../../../utilities/variables/modulfConfigurationKeys";
import { isDocumentWithIdAndProcessMetadata } from "../../workflows/data/processMetadata";

/**
 * Komponente zum Anzeigen eines Prozess Wizards in den Fachverfahren.
 *
 * Dokumentation getA12: Form-Engine (Features > Engines > Form-Engine) zum Verständnis der Integration
 * der Form Engine in den Client.
 */
export function ProcessWizard(props: { activityId: string }) {
	const dispatch = useDispatch();

	// Selektiere den aktuellen Form-Engine State vom Redux Store
	const engineState = useSelector(FormEngineSelectors.engineState(props.activityId));
	const formModel = engineState && ModelSelectors.formModel()(engineState);

	// Alle anzuzeigenden Prozessschritte aus dem Fachverfahrens-Manifest holen
	const allTasks = useAllTasksFromManifest();

	const screenState = engineState && UiStateSelectors.currentScreenLocation()(engineState);
	const currentScreen = screenState && screenState.locationPath[0].elementName;

	// Dokument enthält Information, in welchem Prozessschritt wir gerade stehen
	const currentTaskKey = getCurrentTaskKey(engineState?.data.document);

	const wizardContainerRef = useRef<HTMLDivElement>(null);

	// Setzt den Fokus auf den aktiven Wizard-Schritt wenn der Screen wechselt
	useEffect(() => {
		if (!currentScreen) {
			return;
		}
		const selectedButton = wizardContainerRef.current?.querySelector<HTMLElement>('[aria-current="step"]');
		selectedButton?.focus();
	}, [currentScreen]);

	function handleOnStepClick(taskIndex: number): void {
		if (!isDocumentWithIdAndProcessMetadata(engineState?.data.document) || !engineState) {
			return;
		}

		const fachverfahrenHistory = engineState.data.document.root.processMetadata.fachverfahrenHistory;
		// Behandelt den Bug, dass der erste Taskkey nach Antrag zwei Mal in der History vorhanden war
		const uniqueHistory = fachverfahrenHistory
			? Array.from(new Set(fachverfahrenHistory.map(item => item.entry)))
			: undefined;

		const screenName = uniqueHistory?.[taskIndex];
		const taskKeys = allTasks[taskIndex].key;
		const screens = formModel?.content.screens;

		if (!screens || !screenName) {
			return;
		}

		const screenToUse = getExistingScreenToUse(screenName, screens, taskKeys);

		// Wechsel zum angeklickten Prozessschritt (z.B. von "Abschluss" zu "Prüfung")
		dispatch(
			FormEngineActions.command({
				activityId: props.activityId,
				engineEvent: Commands.changeScreen({
					screenName: screenToUse // Nur Screens nutzen, die im Form Model enthalten sind
				})
			})
		);
	}

	const getSelected = useCallback(
		(task: Task) => {
			if (!currentScreen) {
				return;
			}

			return taskKeysIncludeScreenName(task.key, currentScreen);
		},
		[currentScreen]
	);

	const getDisabled = useCallback(
		(index: number) => {
			if (!currentTaskKey) {
				return;
			}

			const currIndex = allTasks.findIndex(task => taskKeysIncludeTaskId(task.key, currentTaskKey));

			return index > currIndex;
		},
		[currentTaskKey, allTasks]
	);

	const MIN_STEP_WIDTH_PX = 150;

	return (
		<div
			ref={wizardContainerRef}
			className={addPrefix("-u-width-full")}
			style={{
				overflowX: "auto",
				paddingBottom: "6px",
				scrollbarColor: "#4e5965 #e0e4e7",
				scrollbarWidth: "thin"
			}}
		>
			<div style={{ minWidth: `${allTasks.length * MIN_STEP_WIDTH_PX}px` }}>
				<Wizard truncate>
					{allTasks.map((task, index) => {
						return (
							<Wizard.Step
								key={task.key[0].taskId}
								label={task.label}
								selected={getSelected(task)}
								onClick={!getSelected(task) ? () => handleOnStepClick(index) : undefined}
								/** Disable alle Schritte die nach dem aktuellen Prozessschritt kommen */
								disabled={getDisabled(index)}
								title={task.label}
							/>
						);
					})}
				</Wizard>
			</div>
		</div>
	);
}

function taskKeysIncludeTaskId(taskKeys: TaskKey[], taskId: string): boolean {
	return taskKeys.filter(key => key.taskId === taskId).length > 0;
}

export function taskKeysIncludeScreenName(taskKeys: TaskKey[], screenName: string): boolean {
	return taskKeys.filter(key => key.screenName === screenName).length > 0;
}

/**
 * Im Fachverfahrens-Manifest sind alle Tasks des Prozesses hinterlegt. Dadurch wissen wir, welche Steps der Wizard
 * anzeigen muss. Diese Hilfsfunktion sucht diese Annotation aus dem Fachverfahrens-Manifest heraus.
 */
export function useAllTasksFromManifest(): Task[] {
	const tasksAnnotation = useConfigValueFromFVManifest(WIZARD_TASKS_FVMANIFEST_KEY);
	return tasksAnnotation ? JSON.parse(tasksAnnotation) : [];
}

function getCurrentTaskKey(document: object | undefined): string | undefined {
	if (!isDocumentWithIdAndProcessMetadata(document)) {
		return undefined;
	}

	return document.root.processMetadata.task.key;
}

export const getExistingScreenToUse = (
	screenName: string,
	screens: readonly FormModel.Screen[],
	taskKeys: TaskKey[]
): string => {
	const filteredScreens = screens
		.filter(screen => taskKeysIncludeScreenName(taskKeys, screen.name))
		.map(screen => screen.name);
	if (filteredScreens?.includes(screenName)) {
		return screenName;
	} else if (filteredScreens?.length === 1) {
		return filteredScreens[0];
	} else if (filteredScreens?.includes(screenName.substring(screenName.indexOf("AntragAbschliessen")))) {
		return screenName.substring(screenName.indexOf("AntragAbschliessen"));
	} else {
		throw new Error("Screen not available!");
	}
};
